import { useEffect, useState } from 'react';
import {
  AgxBodyText,
  AgxColumn,
  AgxLabel,
  AgxRadio,
  AgxTextInput,
  formatCurrency,
  Images,
  SellersDisclosurePreparedBy,
  SellersDisclosurePreparedByModel,
  SellersDisclosureRequiredSearch,
} from '@urbanx/agx-ui-components';
import './SellersDisclosure.scss';

interface SellersDisclosureProps {
  id: string;
  label?: string;
  onValueChanged: (value: {
    id: string;
    value: SellersDisclosurePreparedByModel;
  }) => void;
  defaultValue?: SellersDisclosurePreparedByModel;
  requiredSearches: SellersDisclosureRequiredSearch[];
  required?: boolean;
  validate?: boolean;
}

const SellersDisclosure = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  requiredSearches,
  required,
  validate,
}: SellersDisclosureProps) => {
  const [preparedByDetails, setPreparedByDetails] = useState<
    SellersDisclosurePreparedByModel | undefined
  >(defaultValue);
  const isThirdPartySelected =
    preparedByDetails?.preparedBy === SellersDisclosurePreparedBy.ThirdParty;
  const isAgentSelected =
    preparedByDetails?.preparedBy === SellersDisclosurePreparedBy.Agent;

  useEffect(() => {
    if (!preparedByDetails) return;

    onValueChanged({ id, value: preparedByDetails });
  }, [preparedByDetails]);

  const renderThirdPartySubContent = () => {
    if (!isThirdPartySelected) return undefined;

    return [
      <div key="third-party-fields">
        <AgxTextInput
          id="thirdPartyName"
          label="Name of third party *"
          noOptionalLabel
          defaultValue={preparedByDetails?.thirdPartyDetails?.thirdPartyName}
          onInputValueChange={({ value }) => {
            setPreparedByDetails({
              ...preparedByDetails,
              thirdPartyDetails: {
                ...preparedByDetails?.thirdPartyDetails,
                thirdPartyName: value,
              },
            });
          }}
          error={
            validate && !preparedByDetails?.thirdPartyDetails?.thirdPartyName
              ? 'Enter third party name'
              : undefined
          }
          stretch
        />
        <AgxTextInput
          id="thirdPartyFee"
          label="Fee *"
          noOptionalLabel
          defaultValue={preparedByDetails?.thirdPartyDetails?.thirdPartyFee}
          onInputValueChange={({ value }) => {
            setPreparedByDetails({
              ...preparedByDetails,
              thirdPartyDetails: {
                ...preparedByDetails?.thirdPartyDetails,
                thirdPartyFee: value,
              },
            });
          }}
          error={
            validate && !preparedByDetails?.thirdPartyDetails?.thirdPartyFee
              ? 'Enter third party fee'
              : undefined
          }
          stretch
        />
      </div>,
    ];
  };

  const renderRequiredSearch = (
    search: SellersDisclosureRequiredSearch,
    index: number
  ) => (
    <li key={index}>
      <AgxBodyText small dataTestId={`agx-agentDisclosureTitle-${index + 1}`}>
        {search.searchName}{' '}
      </AgxBodyText>
      <AgxBodyText
        dataTestId={`agx-agentDisclosureCost-${index + 1}`}
        small
        extraClasses="sellersDisclosure__costs"
      >
        {formatCurrency(search.cost, 2)}{' '}
      </AgxBodyText>
      <AgxBodyText
        small
        dataTestId={`agx-agentDisclosureApplicable-${index + 1}`}
      >
        {search.isOptional ? '(if applicable)' : ''}
      </AgxBodyText>
    </li>
  );

  const renderAgentSubContent = () => {
    if (!isAgentSelected) return undefined;

    return [
      <AgxColumn
        extraClasses="sellersDisclosure__requiredSearches"
        key="required-searches"
      >
        <AgxBodyText small dataTestId="agx-agentDisclosure-description">
          The below searches are required. Please ensure you include them in
          your marketing package.
        </AgxBodyText>
        <ul>{requiredSearches.map(renderRequiredSearch)}</ul>
      </AgxColumn>,
    ];
  };

  return (
    <div className="agxRadio sellersDisclosure" data-testid={id}>
      <AgxLabel medium>{label}</AgxLabel>
      <div className="agxRadio-group">
        <AgxRadio
          id={`${id}-vendorSolicitor`}
          label="Vendor's Solicitor"
          onCheckChanged={() =>
            setPreparedByDetails({
              preparedBy: SellersDisclosurePreparedBy.VendorSolicitor,
              thirdPartyDetails: undefined
            })
          }
          checked={preparedByDetails?.preparedBy === SellersDisclosurePreparedBy.VendorSolicitor}
        />
        <AgxRadio
          id={`${id}-thirdParty`}
          label="Third Party"
          onCheckChanged={() =>
            setPreparedByDetails({
              ...preparedByDetails,
              preparedBy: SellersDisclosurePreparedBy.ThirdParty,
            })
          }
          checked={isThirdPartySelected}
          subContent={renderThirdPartySubContent()}
        />

        <AgxRadio
          id={`${id}-agent`}
          label="Agent"
          onCheckChanged={() =>
            setPreparedByDetails({
              preparedBy: SellersDisclosurePreparedBy.Agent,
              thirdPartyDetails: undefined,
            })
          }
          checked={isAgentSelected}
          subContent={renderAgentSubContent()}
        />
        <AgxRadio
          id={`${id}-vendor`}
          label="Exempt"
          onCheckChanged={() =>
            setPreparedByDetails({
              preparedBy: SellersDisclosurePreparedBy.Exempt,
              thirdPartyDetails: undefined,
            })
          }
          checked={preparedByDetails?.preparedBy === SellersDisclosurePreparedBy.Exempt}
        />
      </div>
      {validate && required && !preparedByDetails?.preparedBy && (
        <div className="errorMessage">
          <Images.AlertCircle />
          <AgxBodyText small extraClasses="error" dataTestId={`error-${id}`}>
            {'Select option'}
          </AgxBodyText>
        </div>
      )}
    </div>
  );
};

export default SellersDisclosure;
